import { last } from 'rxjs';
import { Injectable } from '@angular/core';
import { DriverTemplate } from '../driver-template';

@Injectable({
  providedIn: 'root'
})
export class MouseService extends DriverTemplate {

  // This service is used to control follow the position of the mouse cursor on the screen.
  // and, if enabled, intercept mouse events.

  private lastMousePosition: { x: number, y: number } = { x: 0, y: 0 };
  private mouseWheelAbsY: number = 0;

  constructor() {
    super();
    this.myStatus = {
      enabled: false,
      running: false,
      capturing: false
    };
    // this.captureResultCallback = (result) => {
    //   console.log('Mouse position captured:', result);
    // };
  }

  private mouseMoveHandler = (event: MouseEvent) => {
    if (this.myStatus.capturing) {
      const x = event.clientX / window.innerWidth;
      const y = event.clientY / window.innerHeight;

      // send if the mouse position has changed significantly
      if (Math.abs(this.lastMousePosition.x - x) > 0.05 || Math.abs(this.lastMousePosition.y - y) > 0.05) {
        this.lastMousePosition = { x, y };
        // Capture mouse position
        this.captureResultCallback({
          captureTime: Date.now(),
          mouseData: {
            event: 'mouseMoved',
            coordinates: this.lastMousePosition,
            // Test if left, right or middle button is pressed considering multiple buttons can be pressed at the same time
            mouseLeftButton: event.buttons & 1 ? true : false, // Left button
            mouseRightButton: event.buttons & 2 ? true : false, // Right button
            mouseMiddleButton: event.buttons & 4 ? true : false, // Middle button
            mouseWheelAbsY: this.mouseWheelAbsY // Include the accumulated scroll delta
          }
        });
      }
    }
  };

  private clickHandler = (event: MouseEvent) => {
    if (this.myStatus.capturing) {
      // Ignore if events happen on the top 60 pixels of the screen
      if (event.clientY < 60) {
        return true; // Ignore clicks in the top 60 pixels
      }
      event.preventDefault(); // Prevent default click behavior if needed
      event.stopPropagation(); // Stop the event from propagating further

      const x = event.clientX / window.innerWidth;
      const y = event.clientY / window.innerHeight;
      this.lastMousePosition = { x, y };

      // Optionally capture click events
      // mouseDataHandler(data: {captureTime: number; mouseData: MouseDriverData})
      this.captureResultCallback({
        captureTime: Date.now(),
        mouseData: {
          event: 'mouseClicked',
          coordinates: this.lastMousePosition,
          // Test if left, right or middle button is pressed considering multiple buttons can be pressed at the same time
          mouseLeftButton: event.buttons & 1 ? true : false, // Left button
          mouseRightButton: event.buttons & 2 ? true : false, // Right button
          mouseMiddleButton: event.buttons & 4 ? true : false, // Middle button 
          mouseWheelAbsY: this.mouseWheelAbsY // Include the accumulated scroll delta         
        }
      });
      return false; // Prevent further propagation of the event
    }
    return true; // Allow the event to propagate if not capturing
  };

  private wheelHandler = (event: WheelEvent) => {
    if (this.myStatus.capturing) {
      // Capture mouse wheel events
      this.mouseWheelAbsY += event.deltaY;
      // Normalize the accumulated scroll delta to a value between 0 and 1
      this.mouseWheelAbsY = Math.max(0, Math.min(this.mouseWheelAbsY, window.innerHeight)); // Clamp to window height
      // Capture the mouse wheel event
      this.captureResultCallback({
        captureTime: Date.now(),
        mouseData: {
          event: 'mouseScrolled',
          coordinates: this.lastMousePosition,
          mouseLeftButton: event.buttons & 1 ? true : false, // Left button
          mouseRightButton: event.buttons & 2 ? true : false, // Right button
          mouseMiddleButton: event.buttons & 4 ? true : false, // Middle button
          mouseWheelAbsY: this.mouseWheelAbsY // Include the accumulated scroll delta
        }
      });
    }
  }

  async start(options?: any): Promise<boolean> {
    if (this.myStatus.running) {
      return false;
    }

    try {
      window.addEventListener('mousemove', this.mouseMoveHandler);
      window.addEventListener('mousedown', this.clickHandler);
      window.addEventListener('mouseup', this.clickHandler);
      //window.addEventListener('click', this.clickHandler); // Capture click events to prevent its propagation
      window.addEventListener('wheel', this.wheelHandler); // Capture mouse wheel events if needed

      // Disable pointer events on the document to prevent interaction with other elements
      // This is useful to prevent the user from interacting with the page while capturing mouse events
      document.documentElement.style.setProperty('--data-grid-pointer-events', 'none');

      // Set the status to enabled and running

      this.myStatus.enabled = true;
      this.myStatus.running = true;
      this.myStatus.capturing = true;
      
      return true;
    } catch (error) {
      console.error('Failed to start mouse tracking:', error);
      return false;
    }
  }

  async stop(): Promise<boolean> {
    if (!this.myStatus.running) {
      return false;
    }

    try {

      window.removeEventListener('mousemove', this.mouseMoveHandler);
      window.removeEventListener('mousedown', this.clickHandler);
      window.removeEventListener('mouseup', this.clickHandler);
      //window.removeEventListener('click', this.clickHandler); // Remove click event listener
      //window.removeEventListener('wheel', this.wheelHandler); // Remove mouse wheel event listener if it was added
      document.documentElement.style.setProperty('--data-grid-pointer-events', 'auto');

      this.myStatus.running = false;
      this.myStatus.capturing = false;
      
      return true;
    } catch (error) {
      console.error('Failed to stop mouse tracking:', error);
      return false;
    }
  }

  status() {
    return this.myStatus;
  }

  isEnabled() {
    return this.myStatus.enabled;
  }

  isRunning() {
    return this.myStatus.running;
  }

  isCapturing() {
    return this.myStatus.capturing;
  }

}
